import { useMemo, useState } from 'react'
import {
  Button,
  Card,
  FilterChip,
  Icon,
  PageHead,
  RoleChip,
  Skeleton,
  StateBlock,
  type IconName,
  type Tone,
} from '@w7/ui'
import { useActivity, useSession } from '@/app/queries'
import { AccountCell } from '@/app/AppShell'
import { AccountLink, Amount, AsOf } from '@/app/bits'
import { count, EMPTY, hm, isoDay, ll } from '@/lib/format'
import { ROLE_LABEL, ROLES, type Account, type ActivityEvent, type ActivityKind, type Role } from '@/data/types'

/**
 * Activity — replaces "All Logs".
 *
 * The original was a flat table that reprinted the full date on every row, so
 * answering "what happened yesterday" meant reading timestamps one at a time,
 * and there was no way to narrow to one kind of event: credit movements, new
 * accounts and sign-ins all sat in one undifferentiated list. Here the day is a
 * heading that stays pinned while you read under it, and the kind is a filter.
 *
 * What this screen is NOT: a monitoring surface. It records what happened on
 * your downline and who did it, because that is what an operator needs when an
 * account holder asks about a movement. It carries no scores, no clustering, no
 * "unusual" framing — surveillance and fraud are an explicit architectural
 * non-goal of this console (specs/features/agent-console.yaml non_goals[0]), so
 * device and IP appear here as provenance on an audit entry and nothing else.
 */

/** The trail is a recent window, not the whole history. Stated in the UI, not implied. */
const FEED_LIMIT = 120

type ActivityRow = ActivityEvent & { actor: Account | null; subject: Account | null }

interface KindSpec {
  /** Plural, because it labels a filter over a set of entries. */
  label: string
  icon: IconName
  tone: Tone
  /** What would be here, and what puts it here. Shown when the filter is empty. */
  emptyTitle: string
  emptyBody: string
}

const KINDS: Record<ActivityKind, KindSpec> = {
  transfer: {
    label: 'Credit moved',
    icon: 'transfers',
    tone: 'brand',
    emptyTitle: 'No credit has moved',
    emptyBody:
      'Every give and take between you and your downline is recorded here once the ledger confirms it, with the amount and who authorised it.',
  },
  'account-created': {
    label: 'Accounts created',
    icon: 'plus',
    tone: 'info',
    emptyTitle: 'No accounts have been created',
    emptyBody:
      'Each agent and member added anywhere beneath you lands here, naming the account that created it. Add one from Create.',
  },
  'limit-changed': {
    label: 'Limits changed',
    icon: 'sliders',
    tone: 'warn',
    emptyTitle: 'No credit limits have changed',
    emptyBody:
      'Raising or lowering an account’s credit limit is recorded here with the old and new ceiling, so a rejected bet can be traced to the change that caused it.',
  },
  'status-changed': {
    label: 'Status changed',
    icon: 'lock',
    tone: 'warn',
    emptyTitle: 'No account has changed status',
    emptyBody:
      'Suspending, reactivating or closing an account beneath you is recorded here, with who did it.',
  },
  login: {
    label: 'Sign-ins',
    icon: 'user',
    tone: 'muted',
    emptyTitle: 'No sign-ins recorded',
    emptyBody:
      'Sign-ins by you and by the accounts beneath you appear here, with the device and IP the session came from.',
  },
  'password-reset': {
    label: 'Password resets',
    icon: 'unlock',
    tone: 'muted',
    emptyTitle: 'No passwords have been reset',
    emptyBody:
      'A password you reset for an account beneath you is recorded here. The password itself is never shown or stored in the trail.',
  },
  settlement: {
    label: 'Settlements',
    icon: 'pnl',
    tone: 'profit',
    emptyTitle: 'Nothing has been settled',
    emptyBody:
      'Settlements booked against your downline appear here as they clear, alongside the credit movements they trigger.',
  },
}

/** Stable chip order — the ladder of the spec, not whatever the feed happened to contain. */
const KIND_ORDER: ActivityKind[] = [
  'transfer',
  'account-created',
  'limit-changed',
  'status-changed',
  'login',
  'password-reset',
  'settlement',
]

/**
 * A member profile only opens for accounts inside your own subtree. An entry
 * about your own account names the agent above you, and that account is ABSENT
 * to you — linking it would send you to a "does not exist" page.
 */
const profileHref = (account: Account, selfId: string | undefined): string | undefined =>
  selfId && (account.id === selfId || account.path.includes(selfId)) ? `/member/${account.id}` : undefined

/**
 * Detail text, with one rewrite: the creation entry carries a raw role id
 * ("Role: player"), and the leaf role renders as MEMBER everywhere in this
 * console — ROLE_LABEL is the only thing allowed to name a tier.
 */
function detailText(row: ActivityRow): string | undefined {
  const raw = row.detail?.trim()
  if (!raw) return undefined
  const match = /^Role:\s*(.+)$/.exec(raw)
  const id = match?.[1]
  if (!id) return raw
  const isRole = (ROLES as readonly string[]).includes(id)
  return isRole ? `Role: ${ROLE_LABEL[id as Role]}` : raw
}

export function ActivityPage() {
  const { data: session } = useSession()
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useActivity(FEED_LIMIT)

  /** Empty set means everything — no filter is the default, not a hidden one. */
  const [kinds, setKinds] = useState<ReadonlySet<ActivityKind>>(() => new Set())

  const selfId = session?.account.id
  const unavailable = data?.unavailable
  const asOf = data?.asOf ?? (dataUpdatedAt || undefined)
  const rows: ActivityRow[] = useMemo(() => data?.rows ?? [], [data?.rows])

  const counts = useMemo(() => {
    const map = new Map<ActivityKind, number>()
    for (const row of rows) map.set(row.kind, (map.get(row.kind) ?? 0) + 1)
    return map
  }, [rows])

  const visible = useMemo(
    () => (kinds.size === 0 ? rows : rows.filter((row) => kinds.has(row.kind))),
    [rows, kinds],
  )

  /**
   * Day buckets, in feed order. The heading renders from the first entry's own
   * timestamp rather than from the bucket key — an ISO day string parses as UTC
   * midnight, which would print the previous date west of Greenwich.
   */
  const days = useMemo(() => {
    const out: { key: string; at: string; items: ActivityRow[] }[] = []
    for (const row of visible) {
      const key = isoDay(row.at)
      const last = out[out.length - 1]
      if (last && last.key === key) last.items.push(row)
      else out.push({ key, at: row.at, items: [row] })
    }
    return out
  }, [visible])

  const toggle = (kind: ActivityKind) =>
    setKinds((previous) => {
      const next = new Set(previous)
      if (next.has(kind)) next.delete(kind)
      else next.add(kind)
      return next
    })


  const onlyKind = kinds.size === 1 ? [...kinds][0] : undefined
  const empty = onlyKind ? KINDS[onlyKind] : null

  return (
    <>
      <PageHead
        title="Activity"
        subtitle="Who did what and when — operational audit for your subtree, never member surveillance"
        actions={
          <>
            {/* No push and no polling on this feed — it says so instead of
                looking live and quietly going stale. */}
            <AsOf at={asOf} />
            {data?.martStale ? (
              <span className="w7-dimmer" style={{ fontSize: 12 }}>
                Mart marked stale
              </span>
            ) : null}
            <Button icon="refresh" variant="secondary" busy={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </>
        }
      />

      {unavailable ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="info" title="No data found">
            {unavailable.reason}
            {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null} The activity
            endpoint returns 503 until slice a7 — there is no audit mart and no actor field in
            hierarchy events today. The console will not invent a feed.
          </StateBlock>
        </div>
      ) : null}

      {/* -------------------------------------------------------- filters -- */}
      <div className="w7-row w7-row-wrap" style={{ marginBottom: 'var(--w7-s4)' }}>
        <FilterChip active={kinds.size === 0} count={rows.length} onClick={() => setKinds(new Set())}>
          Everything
        </FilterChip>
        {KIND_ORDER.map((kind) => (
          <FilterChip
            key={kind}
            active={kinds.has(kind)}
            count={counts.get(kind) ?? 0}
            onClick={() => toggle(kind)}
          >
            {KINDS[kind].label}
          </FilterChip>
        ))}
        <span className="w7-spacer" />
        <span className="w7-dim" style={{ fontSize: 12 }}>
          {unavailable
            ? 'Waiting for audit source (a7)'
            : isLoading
              ? 'Loading'
              : `${count(visible.length)} of ${count(rows.length)} entries`}
        </span>
      </div>

      {/* ------------------------------------------------------- the trail -- */}
      <Card
        title="What happened on your downline"
        subtitle={
          unavailable
            ? 'Ledger and hierarchy audit rows will land here once slice a7 exposes them.'
            : 'Newest first. Operator actions beneath you — creates, credit, settlements. Sign-ins need identity audit (CREATE).'
        }
        flush
        footer={
          <span>
            {unavailable
              ? 'OpenAPI ActivityEntry has no device or IP fields — and this console will never add member surveillance.'
              : rows.length < FEED_LIMIT
                ? 'Everything recorded so far.'
                : `The most recent ${count(FEED_LIMIT)} entries.`}{' '}
            {data?.provenanceFromAudit
              ? 'Actor comes from the audit envelope (a7), not from inventing who did it.'
              : 'Device and IP on demo rows are session provenance only — not risk scoring.'}
          </span>
        }
      >
        {unavailable ? (
          <StateBlock icon="activity" title="No audit entries yet">
            {unavailable.reason} When a7 lands, rows are ActivityEntry: action, actor, optional
            subject and amount — never member device fingerprints or IP clustering.
          </StateBlock>
        ) : isLoading ? (
          <TimelineSkeleton />
        ) : days.length === 0 ? (
          <StateBlock
            icon={empty ? empty.icon : 'activity'}
            title={empty ? empty.emptyTitle : kinds.size > 1 ? 'Nothing matches those filters' : 'Nothing has happened yet'}
            actions={
              kinds.size > 0 ? (
                <Button variant="secondary" onClick={() => setKinds(new Set())}>
                  Show everything
                </Button>
              ) : undefined
            }
          >
            {empty
              ? empty.emptyBody
              : kinds.size > 1
                ? 'No entry in this window is one of the kinds you picked. Clear a filter to widen the trail.'
                : 'Credit movements, new accounts, limit changes and settlements across your downline all land here as they happen.'}
          </StateBlock>
        ) : (
          <ol className="w7-timeline">
            {days.map((day) => (
              <li key={day.key}>
                <h3 className="w7-timeline-day">
                  {ll(day.at)}
                  <span className="w7-timeline-daycount">
                    {count(day.items.length)} {day.items.length === 1 ? 'entry' : 'entries'}
                  </span>
                </h3>
                <ol className="w7-timeline-items">
                  {day.items.map((row) => (
                    <Entry
                      key={row.id}
                      row={row}
                      selfId={selfId}
                      hideProvenance={Boolean(data?.provenanceFromAudit)}
                    />
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  )
}

/* ------------------------------------------------------------------ entry -- */

function Entry({
  row,
  selfId,
  hideProvenance,
}: {
  row: ActivityRow
  selfId: string | undefined
  hideProvenance?: boolean
}) {
  const spec = KINDS[row.kind]
  const detail = detailText(row)
  const { actor, subject } = row
  const showSubject = subject !== null && subject.id !== actor?.id

  return (
    <li className="w7-timeline-item">
      <span className="w7-timeline-mark" data-tone={spec.tone === 'muted' ? undefined : spec.tone}>
        <Icon name={spec.icon} size={15} />
      </span>

      <div className="w7-timeline-body">
        <p className="w7-timeline-summary">{row.summary}</p>
        {detail ? <p className="w7-timeline-detail">{detail}</p> : null}

        <span className="w7-timeline-who">
          {actor ? (
            <AccountCell
              username={actor.username}
              displayName={actor.displayName}
              role={actor.role}
              to={profileHref(actor, selfId)}
            />
          ) : (
            <span className="w7-dim">{EMPTY}</span>
          )}
          {showSubject ? (
            <>
              <Icon name="arrow-right" size={13} className="w7-dimmer" />
              <SubjectRef account={subject} selfId={selfId} />
            </>
          ) : null}

          <span className="w7-timeline-source">
            {hideProvenance ? (
              <span>Audit source (no device/IP on contract)</span>
            ) : (
              <>
                {row.device ? <span>{row.device}</span> : null}
                {row.ip ? <span>{row.ip}</span> : null}
                {!row.device && !row.ip ? <span>Source not recorded</span> : null}
              </>
            )}
          </span>
        </span>
      </div>

      <div className="w7-timeline-aside">
        <span className="w7-timeline-time">{hm(row.at)}</span>
        {/* Neutral on purpose. An audit entry records how much moved, not a
            result — green and red stay reserved for settled P&L everywhere in
            this console, and colouring a give red would say the opposite. */}
        {typeof row.amount === 'number' ? <Amount value={row.amount} /> : null}
      </div>
    </li>
  )
}

function SubjectRef({ account, selfId }: { account: Account; selfId: string | undefined }) {
  const href = profileHref(account, selfId)
  return (
    <span className="w7-row" style={{ gap: 6, minWidth: 0 }}>
      {href ? (
        <AccountLink id={account.id} username={account.username} />
      ) : (
        <span className="w7-truncate">{account.username}</span>
      )}
      <RoleChip role={account.role} label={ROLE_LABEL[account.role]} />
    </span>
  )
}

/* --------------------------------------------------------------- loading -- */

/**
 * Skeletons at the real entry height, so the page does not resize under the
 * reader's cursor the moment the trail arrives.
 */
function TimelineSkeleton() {
  return (
    <ol className="w7-timeline" aria-hidden="true">
      <li>
        <p className="w7-timeline-day">
          <Skeleton width={104} height={11} />
        </p>
        <ol className="w7-timeline-items">
          {Array.from({ length: 7 }, (_, i) => (
            <li key={i} className="w7-timeline-item">
              <Skeleton width={28} height={28} radius={999} />
              <span className="w7-timeline-body">
                <Skeleton width={`${44 + ((i * 13) % 34)}%`} height={13} />
                <Skeleton width={264} height={30} radius={6} />
              </span>
              <Skeleton width={64} height={13} />
            </li>
          ))}
        </ol>
      </li>
    </ol>
  )
}
